import { DndModule } from 'ngx-drag-drop';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DateRangeComponent } from '../date-range/date-range.component';
import { MatLegacyButtonModule } from '@angular/material/legacy-button';
import { MatLegacyMenuModule } from '@angular/material/legacy-menu';
import { MatDividerModule } from '@angular/material/divider';
import { ReportTypes } from '@mode/shared/contract-common';
import { IconButtonComponent, IconComponent, TooltipDirective } from '@mode/capra';
import { NumericRangeComponent } from '../numeric-range/numeric-range.component';
import { MultiSelectComponent, SELECT_ALL_VALUE } from '../multi-select/multi-select.component';
import { DropdownComponent } from './../dropdown/dropdown.component';
import { ReportFilterControlHelpers, ReportFilterDomainResult } from '@mode/filters/util';
import { Attr } from '@mode-switch/model';
import { SingleSelectComponent } from '../single-select/single-select.component';
import {
  EncContinuousFilterField,
  EncDiscreteBooleanFilterField,
  EncDiscreteFilterField,
  EncFilterField,
  SelectionMode,
} from '@mode-vegas/vegas';

@Component({
  selector: 'mode-filter-container',
  standalone: true,
  imports: [
    CommonModule,
    DateRangeComponent,
    MatLegacyButtonModule,
    MatLegacyMenuModule,
    IconComponent,
    IconButtonComponent,
    TooltipDirective,
    NumericRangeComponent,
    DropdownComponent,
    MultiSelectComponent,
    SingleSelectComponent,
    MatDividerModule,
    DndModule,
  ],
  templateUrl: './filter-container.component.html',
  styleUrls: ['./filter-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FilterContainerComponent implements OnChanges {
  @Input() filter!: ReportTypes.ReportFilterControl;
  @Input() filterOption!: ReportTypes.ReportFilterOptions;
  @Input() isLoading = true;
  @Input() showSettingsMenu = false;
  @Input() showDropdownToggle = false;
  @Input() dragAndDropEnabled = false;
  @Input() displayRelevantValuesEnabled = false;
  @Input() isOrphan = false;
  @Input() isCalc = false;
  @Input() tooManySelected = false;

  @Output() updateReportFilterType = new EventEmitter<{
    field: ReportTypes.ReportFilterControl;
    type: ReportTypes.ReportFilterType;
  }>();
  @Output() updateSelectControlType = new EventEmitter<{
    field: ReportTypes.ReportFilterControl;
    type: ReportTypes.SelectControlType;
  }>();
  @Output() showAll = new EventEmitter<{ token: string; shouldShowAll: boolean }>();
  @Output() showRelevantValues = new EventEmitter<{ filterToken: string; showRelevantValues: boolean }>();
  @Output() moveFilterToBottom = new EventEmitter<ReportTypes.ReportFilterControl>();
  @Output() moveFilterToTop = new EventEmitter<ReportTypes.ReportFilterControl>();
  @Output() moveFilterDown = new EventEmitter<ReportTypes.ReportFilterControl>();
  @Output() moveFilterUp = new EventEmitter<ReportTypes.ReportFilterControl>();
  @Output() resetFilter = new EventEmitter<string>();
  @Output() deleteFilter = new EventEmitter<string>();
  @Output() selectChange = new EventEmitter<{ token: string; selection: ReportTypes.DiscreteFilterSelectionTypes }>();
  @Output() rangeChange = new EventEmitter<{ filter: EncContinuousFilterField; token: string }>();
  @Output() multiSelectChange = new EventEmitter<{
    filter: ReportTypes.MultiValueFilterControl;
    selection: ReportTypes.DiscreteFilterSelectionTypes;
    searchTerm: string;
  }>();
  @Output() dragStarted = new EventEmitter<ReportTypes.ReportFilterControl>();
  @Output() dragEnded = new EventEmitter();

  orphanedFilterTooltip = `This filter is no longer valid. Try deleting the filter then adding it again.`;
  tooManySelectionsTooltip = `Current filter configuration might create chart errors due to a long filter query. Try to limit filter selection or use select all.`;
  relevantValueTooltip = `This filter only shows relevant values based on other filter selections`;

  filterAsEncFilterField!: EncFilterField;
  filterOptionsAsDomainResults: ReportFilterDomainResult | null = null;

  get isSingle() {
    return this.filter?.filterType === ReportTypes.ReportFilterType.Single;
  }

  get isMulti() {
    return this.filter?.filterType === ReportTypes.ReportFilterType.Multi;
  }

  get isRange() {
    return this.filter?.filterType === ReportTypes.ReportFilterType.Range;
  }

  get isDropdown() {
    return this.filter?.controlType === ReportTypes.ReportFilterControlType.Dropdown;
  }

  get isList() {
    return this.filter?.controlType === ReportTypes.ReportFilterControlType.List;
  }

  get isShowingAll() {
    return !this.filter?.hideSelectAll;
  }

  get isRelevantValue() {
    return this.filter?.showRelevantValuesOnly;
  }

  get showRelevantValueOption() {
    return (
      // Remove this condition when the feature is enabled by default
      (this.displayRelevantValuesEnabled || this.isRelevantValue) &&
      // Condition to keep after FF removal
      (this.isSingle || this.isMulti)
    );
  }

  get isShowingRangeOption(): boolean {
    const record = ReportFilterControlHelpers.toControl(this.filter);
    return record.isNumericType || (record.isDateType && !record.isDiscreteDateType);
  }

  get rangeFilterOption() {
    if (this.filterOption != null && ReportFilterControlHelpers.isRangeFilterOption(this.filterOption)) {
      return this.filterOption;
    }
  }

  get allowDragAndDrop() {
    return this.showSettingsMenu && this.dragAndDropEnabled;
  }

  get hasWarning() {
    return this.tooManySelected || this.isOrphan;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.filterOption && this.filterOption) {
      switch (this.filterOption.filterType) {
        case ReportTypes.ReportFilterType.Multi:
        case ReportTypes.ReportFilterType.Single:
          this.setFilterDomainResults(this.filterOption.values);
          break;
      }
    }

    if (changes.filter?.currentValue) {
      if (this.filter.filterType === ReportTypes.ReportFilterType.Range) {
        this.filterAsEncFilterField = ReportFilterControlHelpers.toRangeFilterField(this.filter);
        this.filterOptionsAsDomainResults = null;
      } else if (this.filter.filterType === ReportTypes.ReportFilterType.Single) {
        this.filterAsEncFilterField = ReportFilterControlHelpers.toSingleSelectFilterField(this.filter);
      } else {
        this.filterAsEncFilterField = ReportFilterControlHelpers.toMultiSelectFilterField(this.filter);
      }
    }
  }

  setFilterDomainResults(options: ReportTypes.DiscreteValueOption[]) {
    this.filterOptionsAsDomainResults = {
      keys: new Set(options.map((v) => v.value)),
      type: Attr.Type.Discrete,
      values: options.map((v) => v.value),
      // Assume if the first label is a string, then they are all strings
      // If the label not a string, then we don't need to pass them down
      labels: typeof options[0]?.label === 'string' ? options.map((v) => v.label) : undefined,
    };
  }

  onReportFilterTypeClick(newType: string) {
    if (newType !== this.filter.filterType) {
      this.updateReportFilterType.emit({
        field: this.filter,
        type: newType as ReportTypes.ReportFilterType,
      });
    }
  }

  onShowAllClick() {
    this.showAll.emit({ token: this.filter.token, shouldShowAll: this.filter.hideSelectAll ?? false });
  }

  onShowRelevantValues() {
    this.showRelevantValues.emit({
      filterToken: this.filter.token,
      showRelevantValues: !this.filter.showRelevantValuesOnly,
    });
  }

  setDiscreteControlType(controlType: 'list' | 'dropdown') {
    switch (controlType) {
      case 'list':
        if (this.filter.controlType !== ReportTypes.ReportFilterControlType.List) {
          this.updateSelectControlType.emit({ field: this.filter, type: ReportTypes.ReportFilterControlType.List });
        }
        break;
      case 'dropdown':
        if (this.filter.controlType !== ReportTypes.ReportFilterControlType.Dropdown) {
          this.updateSelectControlType.emit({ field: this.filter, type: ReportTypes.ReportFilterControlType.Dropdown });
        }
        break;
    }
  }

  onMoveToBottomClick() {
    this.moveFilterToBottom.emit(this.filter);
  }

  onMoveToTopClick() {
    this.moveFilterToTop.emit(this.filter);
  }

  onMoveDownClick() {
    this.moveFilterDown.emit(this.filter);
  }

  onMoveUpClick() {
    this.moveFilterUp.emit(this.filter);
  }

  onResetFilterClick() {
    this.resetFilter.emit(this.filter.token);
  }

  onDeleteFilterClick() {
    this.deleteFilter.emit(this.filter.token);
  }

  onSelectFilterChange(filter: EncDiscreteFilterField | EncDiscreteBooleanFilterField) {
    let selection: ReportTypes.DiscreteFilterSelectionTypes;

    if (filter.selection.mode === 'ALL') {
      selection = {
        type: ReportTypes.DiscreteSelectionType.All,
      };
    } else {
      selection = {
        type: ReportTypes.DiscreteSelectionType.Select,
        value: filter.selection.selected[0],
        selected: true,
      };
    }

    this.selectChange.emit({
      token: this.filter.token,
      selection,
    });
  }

  onMultiSelectFilterChange(
    filter: EncDiscreteFilterField | EncDiscreteBooleanFilterField,
    selectedValue: ReportTypes.ReportFilterValue,
    isChecked: boolean,
    searchTerm: string
  ) {
    if (ReportFilterControlHelpers.isMultiValueControlFilter(this.filter)) {
      let selection: ReportTypes.DiscreteFilterSelectionTypes;

      if (filter.selection.mode === SelectionMode.ALL) {
        selection = {
          type: ReportTypes.DiscreteSelectionType.All,
        };
      } else {
        if (selectedValue === SELECT_ALL_VALUE) {
          selection = {
            type: isChecked ? ReportTypes.DiscreteSelectionType.All : ReportTypes.DiscreteSelectionType.None,
          };
        } else {
          selection = {
            type: ReportTypes.DiscreteSelectionType.Select,
            value: selectedValue,
            selected: isChecked,
          };
        }
      }

      this.multiSelectChange.emit({
        filter: this.filter,
        selection,
        searchTerm,
      });
    }
  }

  onRangeFilterChange(filter: EncContinuousFilterField) {
    if (ReportFilterControlHelpers.isRangeValueControlFilter(this.filter)) {
      this.rangeChange.emit({
        token: this.filter.token,
        filter,
      });
    }
  }

  onDragEnd() {
    this.dragEnded.emit();
  }

  onDragStart(filter: ReportTypes.ReportFilterControl) {
    this.dragStarted.emit(filter);
  }
}
